import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import { Tooltip } from "@mui/material";
import { AppDispatch, RootState } from "../../redux/store";
import {
  createGroupParticipant,
  deleteGroupParticipant,
  getGroupParticipants,
  setGroupAdministrator,
} from "../../redux/actions/fluxoNSimilar";
import { usePaginationState } from "../../hooks/usePaginationState";
import { usePersistedPagination } from "../../hooks/usePersistedPagination";
import { useModalState } from "../../hooks/useModalState";
import { useNotification } from "../../hooks/useNotification";
import { SkeletonWrapper } from "../SkeletonWrapper/sleketonWrapper";
import GenericGrid from "../fluxo-engenheiro/grid/grid";
import { buildGroupParticipantsColumns } from "../../utils/DataGrid/groupParticipantsColumns";
import ContainerError from "../error/containerError";
import { ButtonGeneric } from "../ButtonGeneric/buttonGeneric";
import ModalAprover from "../fluxo-tax/modalAprover/modalAprover";
import Input from "../fluxo-engenheiro/formBasic/Input";
import ModalNovoParticipante from "./ModalNovoParticipante";
import Notification from "../fluxo-tax/Notification/notification";
import Textnotifications from "../../utils/notifications";
import IconAlert from "../../assets/svgs/icon-alert.svg";
import SucessIcon from "../../assets/svgs/sucess.svg";
import PlusButtonFilter from "../../assets/svgs/plusButtonFilter.svg";
import SearchFilterIcon from "../../assets/svgs/searchFilter.svg";
import Lixo from "../../assets/svgs/lixo.svg";
import "./participantesGrupoTable.css";

interface ParticipantesGrupoTableProps {
  groupId: string;
  groupName: string;
  onAdminTransferred?: () => boolean | Promise<boolean>;
}

const EMAIL_FILTER_DEBOUNCE_MS = 400;

const ParticipantesGrupoTable = ({
  groupId,
  groupName,
  onAdminTransferred,
}: ParticipantesGrupoTableProps) => {
  const dispatch = useDispatch<AppDispatch>();
  const groupParticipantsState = useSelector(
    (state: RootState) => state.sessionGroupParticipants,
  );
  const paginationState = usePaginationState();
  const persistedPagination = usePersistedPagination(
    `pagination_grupo_participantes_${groupId}`,
    { page: 1, pageSize: 20 },
  );
  const pagination = { ...paginationState, ...persistedPagination };
  const newParticipantModal = useModalState();
  const deleteParticipantModal = useModalState();
  const promoteAdminModal = useModalState();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedParticipant, setSelectedParticipant] = useState<{
    userId: string;
    email: string;
  } | null>(null);
  const [participantToPromote, setParticipantToPromote] = useState<{
    userId: string;
    email: string;
  } | null>(null);
  const [emailFilter, setEmailFilter] = useState("");
  const [debouncedEmailFilter, setDebouncedEmailFilter] = useState("");
  const previousDebouncedEmailFilter = useRef(debouncedEmailFilter);
  const [notificationIcon, setNotificationIcon] = useState(SucessIcon);
  const {
    notification,
    labelNotification,
    backgroundColor,
    setNotification,
    showNotification,
  } = useNotification();
  const participantsResponse =
    groupParticipantsState.participantsByGroupId[groupId];
  const requestStatus = groupParticipantsState.statusByGroupId[groupId] ?? "idle";

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedEmailFilter(emailFilter.trim());
    }, EMAIL_FILTER_DEBOUNCE_MS);

    return () => clearTimeout(timeoutId);
  }, [emailFilter]);

  useEffect(() => {
    const filterChanged =
      previousDebouncedEmailFilter.current !== debouncedEmailFilter;
    previousDebouncedEmailFilter.current = debouncedEmailFilter;

    if (filterChanged && pagination.page !== 1) {
      pagination.setPage(1);
      return;
    }

    void dispatch(
      getGroupParticipants({
        groupId,
        page: pagination.page,
        size: pagination.pageSize,
        email: debouncedEmailFilter || undefined,
      }),
    );
  }, [dispatch, groupId, pagination.page, pagination.pageSize, debouncedEmailFilter]);

  useEffect(() => {
    if (!participantsResponse) return;

    const participants = Array.isArray(participantsResponse.content)
      ? participantsResponse.content
      : [];
    pagination.setRows(
      participants.map((participant) => ({
        ...participant,
        classificationId: participant.userId,
        email: participant.name,
      })),
    );
    pagination.setTotalPages(participantsResponse.page?.totalPages ?? 0);
  }, [participantsResponse]);

  const refreshParticipants = async () => {
    await dispatch(
      getGroupParticipants({
        groupId,
        page: pagination.page,
        size: pagination.pageSize,
        email: debouncedEmailFilter || undefined,
      }),
    );
  };

  const handleCreateParticipant = async (userId: string) => {
    setIsSubmitting(true);

    try {
      await dispatch(createGroupParticipant({ groupId, userId })).unwrap();
      await refreshParticipants();
      setNotificationIcon(SucessIcon);
      showNotification("Participante adicionado com sucesso.");
      newParticipantModal.close();
    } catch (error: any) {
      setNotificationIcon(IconAlert);
      showNotification(
        Textnotifications.getCreateGroupParticipantError(error?.message),
        "#FFD9D9",
      );
      console.error("Erro ao adicionar participante ao grupo:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteParticipant = async () => {
    const participant = selectedParticipant;
    if (!participant) return;

    setIsSubmitting(true);

    try {
      await dispatch(
        deleteGroupParticipant({
          groupId,
          userId: participant.userId,
        }),
      ).unwrap();
      await refreshParticipants();
      setNotificationIcon(SucessIcon);
      showNotification(`Participante "${participant.email}" removido com sucesso.`);
      deleteParticipantModal.close();
      setSelectedParticipant(null);
    } catch (error: any) {
      setNotificationIcon(IconAlert);
      showNotification(
        Textnotifications.getDeleteGroupParticipantError(error?.message),
        "#FFD9D9",
      );
      console.error("Erro ao remover participante do grupo:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSetParticipantAsAdmin = async () => {
    const participant = participantToPromote;
    if (!participant) return;

    setIsSubmitting(true);

    try {
      await dispatch(
        setGroupAdministrator({
          groupId,
          newAdministratorId: participant.userId,
        }),
      ).unwrap();
      promoteAdminModal.close();
      setParticipantToPromote(null);

      const shouldRefresh = onAdminTransferred
        ? await onAdminTransferred()
        : true;

      if (!onAdminTransferred) {
        setNotificationIcon(SucessIcon);
        showNotification(
          `Participante "${participant.email}" agora é o administrador do grupo.`,
        );
      }

      if (shouldRefresh) {
        await refreshParticipants();
      }
    } catch (error: any) {
      setNotificationIcon(IconAlert);
      showNotification(
        Textnotifications.getSetGroupAdministratorError(error?.message),
        "#FFD9D9",
      );
      console.error("Erro ao definir participante como administrador do grupo:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const columns = buildGroupParticipantsColumns({
    renderActions: (row) => {
      if (row.role === "admin") return null;

      return (
        <div className="groupParticipantActions">
          <Tooltip title="Definir como Admin">
            <button
              className="groupParticipantActionButton"
              type="button"
              onClick={() => {
                setParticipantToPromote({
                  userId: row.userId,
                  email: row.email ?? row.name,
                });
                promoteAdminModal.open();
              }}
            >
              Definir como Admin
            </button>
          </Tooltip>
          <Tooltip title="Remover">
            <button
              className="groupParticipantDeleteButton"
              type="button"
              onClick={() => {
                setSelectedParticipant({
                  userId: row.userId,
                  email: row.email ?? row.name,
                });
                deleteParticipantModal.open();
              }}
            >
              <img src={Lixo} alt="Remover" width={16} />
            </button>
          </Tooltip>
        </div>
      );
    },
  });

  if (requestStatus === "error" && !participantsResponse) {
    return (
      <div className="groupParticipantsError" role="alert">
        <ContainerError
          id="group-participants-load-error"
          label="Não foi possível carregar os participantes do grupo."
          icon={IconAlert}
          widthError="100%"
          backgroudError="#FFD9D9"
          colorError="#000000"
        />
      </div>
    );
  }

  return (
    <div className="groupParticipantsTable" data-testid="group-participants-table">
      <SkeletonWrapper loading={requestStatus === "loading" && !participantsResponse} />
      {!(requestStatus === "loading" && !participantsResponse) && (
        <>
          <div className="groupParticipantsActions">
            <div className="groupParticipantsFilter">
              <Input
                label=""
                width="280px"
                placeholder="Buscar por email..."
                img={SearchFilterIcon}
                value={emailFilter}
                onChange={(event) => setEmailFilter(event.target.value)}
                data-testid="input-participants-email-filter"
              />
            </div>
            <ButtonGeneric
              type="button"
              title="Adicionar"
              ariaLabel="Adicionar participante"
              img={PlusButtonFilter}
              style="buttonHeading"
              colorTitle="#ffffff"
              onClick={newParticipantModal.open}
            />
          </div>
          <div className="groupParticipantsGrid">
            <GenericGrid
              titleGrid=" "
              rows={pagination.rows}
              totalPages={pagination.totalPages}
              page={pagination.page}
              columns={columns}
              setCurrentPage={pagination.setPage}
              setPage={pagination.setPage}
              setLoading={pagination.setLoading}
              fullWidth={true}
              enablePageSizeSelector={true}
              pageSize={pagination.pageSize}
              pageSizeOptions={[5, 10, 20, 50, 100]}
              onPageSizeChange={(size) => {
                pagination.setPageSize(size);
                pagination.setPage(1);
              }}
            />
          </div>
        </>
      )}

      {newParticipantModal.isOpen && (
        createPortal(
          <ModalNovoParticipante
            groupName={groupName}
            isSubmitting={isSubmitting}
            onClose={newParticipantModal.close}
            onConfirm={handleCreateParticipant}
          />,
          document.body,
        )
      )}

      {promoteAdminModal.isOpen && participantToPromote &&
        createPortal(
          <ModalAprover
            titleModal="Definir como Administrador"
            titleDescription={
              <div style={{ marginBottom: 20, marginTop: 15 }}>
                <div className="titleAprover">
                  Você deseja definir "{participantToPromote.email}" como
                  administrador deste grupo?
                </div>
                <p className="groupParticipantAdminWarning">
                  Ao confirmar, o administrador atual deste grupo passará a
                  atuar como participante.
                </p>
              </div>
            }
            bodyModal=""
            buttonOne="Confirmar"
            onConfirm={handleSetParticipantAsAdmin}
            buttonTwo="Cancelar"
            onClose={() => {
              promoteAdminModal.close();
              setParticipantToPromote(null);
            }}
            isSubmitting={isSubmitting}
          />,
          document.body,
        )}

      {deleteParticipantModal.isOpen && selectedParticipant &&
        createPortal(
          <ModalAprover
            titleModal="Excluir Participante"
            titleDescription={
              <div
                className="titleAprover"
                style={{ marginBottom: 20, marginTop: 15 }}
              >
                Você deseja excluir o participante "{selectedParticipant.email}"?
              </div>
            }
            bodyModal=""
            buttonOne="Confirmar"
            onConfirm={handleDeleteParticipant}
            buttonTwo="Cancelar"
            onClose={() => {
              deleteParticipantModal.close();
              setSelectedParticipant(null);
            }}
            isSubmitting={isSubmitting}
          />,
          document.body,
        )}

      {notification && (
        createPortal(
          <Notification
            label={labelNotification}
            backgroundError={backgroundColor}
            setNotification={setNotification}
            img={notificationIcon}
          />,
          document.body,
        )
      )}
    </div>
  );
};

export default ParticipantesGrupoTable;